<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import type { VerseSelectionSubmit } from '../../types/verseSelection'
import type { Bookmark } from '../../types/bookmark'
import CascadeSelect from './CascadeSelect.vue'
import BibleReadingFrame from './BibleReadingFrame.vue'
import StrongsPanel from './StrongsPanel.vue'
import BookmarkList from '../BookmarkList.vue'
import NoteList from '../NoteList.vue'
import { useBibleReading } from '../../composables/useBibleReading'
import { useStrongsDictionary } from '../../composables/useStrongsDictionary'
import { useGeonymHighlight } from '../../composables/useGeonymHighlight'
import { useGlobalBookmarks } from '../../composables/useGlobalBookmarks'
import { useGlobalNotes } from '../../composables/useGlobalNotes'
import { useGlobalBibleData } from '../../composables/useGlobalBibleData'
import { useFrameStore } from '../../stores/frameStore'

interface Props {
  frameId?: number
  initialVersionCode?: string | null
  initialBookId?: number | null
  initialChapter?: number | null
}

const props = withDefaults(defineProps<Props>(), {
  frameId: 0,
  initialVersionCode: null,
  initialBookId: null,
  initialChapter: null,
})

const {
  versions,
  books,
  chapters,
  selection,
  loading,
  error,
  verses,
  includeStrong,
  loadVersions,
  selectVersion,
  selectBook,
  selectChapter,
  setIncludeStrong,
  isSelectionComplete,
  isBookSelectDisabled,
  isChapterSelectDisabled,
} = useBibleReading()

const strongsDict = useStrongsDictionary()
const geoHighlight = useGeonymHighlight()

const showBookmarkPanel = ref(false)
const showNoteListPanel = ref(false)

// 互斥的選單狀態：一次只能打開一個
const openSelectMenu = ref<'version' | 'book' | 'chapter' | null>(null)

const isGeoDisabled = computed(() => !isSelectionComplete.value || strongsDict.isEnabled.value)

const { bookmarks, loadBookmarks, addBookmark, removeBookmark } = useGlobalBookmarks()
const { notes, loadNotes, createNote, deleteNote, selectNote } = useGlobalNotes()
const { books: globalBooks, loadBooks } = useGlobalBibleData()
const frameStore = useFrameStore()

const handleVerseSubmit = async (payload: VerseSelectionSubmit) => {
  if (payload.action === 'bookmark') {
    try {
      const book = books.value.find(b => b.id === payload.selection.bookId)
      const bookName = book?.chiFull || '未知'
      const version = versions.value.find(v => v.code === payload.selection.versionCode)
      const versionName = version?.name || '未知'
      await addBookmark(payload, bookName, versionName)
    } catch (err) {
      console.error('Failed to add bookmark:', err)
    }
  } else if (payload.action === 'note') {
    try {
      const book = books.value.find(b => b.id === payload.selection.bookId)
      const bookName = book?.chiFull || '未知'
      const version = versions.value.find(v => v.code === payload.selection.versionCode)
      const versionName = version?.name || '未知'

      // 創建新筆記框架（放在啟動框右側）
      const sourceFrame = frameStore.frames.find(f => f.frameId === props.frameId)
      const noteX = sourceFrame ? Math.min(sourceFrame.x + sourceFrame.w + 40, 4000 - 600) : 600
      const noteY = sourceFrame ? sourceFrame.y : 60
      const noteW = sourceFrame ? sourceFrame.w : 600
      const noteH = sourceFrame ? sourceFrame.h : 500

      const noteFrame = {
        frameId: Date.now(),
        x: noteX,
        y: noteY,
        w: noteW,
        h: noteH,
        title: `${bookName}${payload.selection.chapter}:${payload.verses[0]}（${versionName}）`,
        type: 'note-editor' as const,
        color: sourceFrame?.color ?? ('cyan' as const),
        _noteInfo: {
          versionCode: payload.selection.versionCode!,
          bookId: payload.selection.bookId!,
          chapterNumber: payload.selection.chapter!,
          verses: payload.verses,
        },
      }

      frameStore.addFrame(noteFrame as any)

      // 創建新筆記記錄（空筆記）並立即選擇
      const newNote = await createNote({
        versionCode: payload.selection.versionCode!,
        bookId: payload.selection.bookId!,
        chapterNumber: payload.selection.chapter!,
        verseStart: payload.verses[0],
        content: '',
      })

      // 將新建筆記設置為當前筆記
      selectNote(newNote)
    } catch (err) {
      console.error('Failed to create note frame:', err)
    }
  }
}

const handleSelectNoteFromList = async (note: any) => {
  try {
    // 確保該版本的書籍資料被加載
    if (!globalBooks.value.some(b => b.id === note.bookId)) {
      await loadBooks(note.versionCode)
    }

    const version = versions.value.find(v => v.code === note.versionCode)
    const versionName = version?.name || '未知'
    // 優先使用 books.value，如果為空則使用 globalBooks
    let book = books.value.find(b => b.id === note.bookId)
    if (!book) {
      book = globalBooks.value.find(b => b.id === note.bookId)
    }
    const bookName = book?.chiFull || '未知'

    const sourceFrame = frameStore.frames.find(f => f.frameId === props.frameId)
    const noteX = sourceFrame ? Math.min(sourceFrame.x + sourceFrame.w + 40, 4000 - 600) : 600
    const noteY = sourceFrame ? sourceFrame.y : 60
    const noteW = sourceFrame ? sourceFrame.w : 600
    const noteH = sourceFrame ? sourceFrame.h : 500

    const noteFrame = {
      frameId: Date.now(),
      x: noteX,
      y: noteY,
      w: noteW,
      h: noteH,
      title: `${bookName}${note.chapterNumber}:${note.verseStart}（${versionName}）`,
      type: 'note-editor' as const,
      color: sourceFrame?.color ?? ('cyan' as const),
      _noteInfo: {
        versionCode: note.versionCode,
        bookId: note.bookId,
        chapterNumber: note.chapterNumber,
        verses: [note.verseStart],
      },
      _isReadingMode: true,
    }

    console.log('創建筆記框架進入閱讀模式:', {
      _isReadingMode: (noteFrame as any)._isReadingMode,
      frameId: noteFrame.frameId,
      title: noteFrame.title,
    })

    frameStore.addFrame(noteFrame as any)
    showNoteListPanel.value = false
  } catch (err) {
    console.error('Failed to open note for reading:', err)
  }
}

const handleMorphClick = async (code: string) => {
  try {
    // 打開詞性代碼查詢
    strongsDict.showPanel.value = true
    strongsDict.currentNumber.value = code
    strongsDict.currentWord.value = `<${code}>` // 詞性代碼標記
    strongsDict.loading.value = true
    strongsDict.error.value = null
    strongsDict.currentTestament.value = 'MORPH' as any // 特殊值表示詞性代碼

    const response = await fetch(`/api/bible/morphology/${code}`)
    if (!response.ok) {
      throw new Error('Failed to fetch morphology data')
    }

    const data = await response.json()
    if (data.success && data.data) {
      strongsDict.currentEntry.value = {
        strongsNumber: code,
        originalWord: '',
        chineseDefinition: data.data.chineseDefinition || '詞性代碼',
        englishDefinition: data.data.englishDefinition || '',
        testament: 'OT' as const,
        relatedWords: [],
      }
    }
  } catch (err) {
    strongsDict.error.value = err instanceof Error ? err.message : 'Failed to lookup morphology'
  } finally {
    strongsDict.loading.value = false
  }
}

const handleDeleteBookmark = async (id: number) => {
  try {
    await removeBookmark(id)
  } catch (err) {
    console.error('Failed to delete bookmark:', err)
  }
}

const handleSelectBookmark = async (bookmark: Bookmark) => {
  try {
    // 1. 更新版本選擇（自動加載該版本的書籍）
    await selectVersion(bookmark.versionCode)

    // 2. 確保書籍列表已加載
    if (!books.value.some(b => b.id === bookmark.bookId)) {
      await loadBooks(bookmark.versionCode)
    }

    // 3. 級聯更新：書籍 → 章節 → 自動加載經文
    selectBook(bookmark.bookId)
    selectChapter(bookmark.chapterNumber)

    // 4. 關閉書籤面板
    showBookmarkPanel.value = false

    // 可選：重置選單打開狀態
    openSelectMenu.value = null
  } catch (err) {
    console.error('Failed to navigate to bookmark:', err)
  }
}

const handleSelectMenuOpen = (menu: 'version' | 'book' | 'chapter') => {
  // 切換選單：如果已打開就關閉，否則打開並關閉其他
  if (openSelectMenu.value === menu) {
    openSelectMenu.value = null
  } else {
    openSelectMenu.value = menu
  }
}

const notesForCurrentVerse = computed(() => {
  if (!selection.value.versionCode || !selection.value.bookId || !selection.value.chapter) {
    return []
  }
  return notes.value.filter(n =>
    n.versionCode === selection.value.versionCode &&
    n.bookId === selection.value.bookId &&
    n.chapterNumber === selection.value.chapter
  )
})

const versionOptions = computed(() =>
  versions.value.map(v => ({
    value: v.code,
    label: v.name,
  }))
)

const bookOptions = computed(() =>
  books.value.map(b => ({
    value: b.id,
    label: `${b.chiFull}`,
  }))
)

const chapterOptions = computed(() =>
  chapters.value.map(c => ({
    value: c,
    label: `第 ${c} 章`,
  }))
)

const handleGeoToggle = async () => {
  if (geoHighlight.isEnabled.value) {
    geoHighlight.toggle()
  } else {
    if (selection.value.bookId && selection.value.chapter) {
      await geoHighlight.loadPlaces(selection.value.bookId, selection.value.chapter)
    }
    geoHighlight.toggle()
  }
}

const handleGeoClick = (placeId: string) => {
  console.log('handleGeoClick called with placeId:', placeId)
  console.log('selection:', {
    versionCode: selection.value.versionCode,
    bookId: selection.value.bookId,
    chapter: selection.value.chapter,
  })

  const sourceFrame = frameStore.frames.find(f => f.frameId === props.frameId)
  const mapX = sourceFrame ? Math.min(sourceFrame.x + sourceFrame.w + 40, 4000 - 600) : 600
  const mapY = sourceFrame ? sourceFrame.y : 60
  const mapW = sourceFrame ? sourceFrame.w : 600
  const mapH = sourceFrame ? sourceFrame.h : 500
  const book = books.value.find(b => b.id === selection.value.bookId)
  const bookName = book?.chiFull || ''

  const mapFrame = {
    frameId: Date.now(),
    x: mapX,
    y: mapY,
    w: mapW,
    h: mapH,
    title: `地圖查閱 - ${bookName}${selection.value.chapter}章`,
    type: 'map-lookup' as const,
    color: sourceFrame?.color ?? ('cyan' as const),
    _mapInfo: {
      bookId: selection.value.bookId!,
      chapter: selection.value.chapter!,
      selectedPlaceId: placeId,
    },
  } as any

  console.log('Adding frame with _mapInfo:', mapFrame._mapInfo)
  frameStore.addFrame(mapFrame)
}

watch(
  () => [selection.value.bookId, selection.value.chapter],
  () => {
    if (geoHighlight.isEnabled.value) geoHighlight.toggle()
  }
)

onMounted(async () => {
  await loadVersions()
  loadBookmarks()
  loadNotes()

  if (props.initialVersionCode) {
    await selectVersion(props.initialVersionCode)
  }
  if (props.initialBookId) {
    selectBook(props.initialBookId)
  }
  if (props.initialChapter) {
    selectChapter(props.initialChapter)
  }
})
</script>

<template>
  <div class="bible-reading-toolbar">
    <div class="toolbar-controls">
      <!-- 聖經譯本選單 -->
      <div class="control-group">
        <CascadeSelect
          :model-value="selection.versionCode"
          :options="versionOptions"
          :is-open="openSelectMenu === 'version'"
          placeholder="聖經譯本"
          searchable
          @update:model-value="selectVersion"
          @update:is-open="handleSelectMenuOpen('version')"
        />
      </div>

      <!-- 書信選單 -->
      <div class="control-group">
        <CascadeSelect
          :model-value="selection.bookId"
          :options="bookOptions"
          :is-open="openSelectMenu === 'book'"
          placeholder="書信"
          :disabled="isBookSelectDisabled"
          @update:model-value="selectBook"
          @update:is-open="handleSelectMenuOpen('book')"
        />
      </div>

      <!-- 章節選單 -->
      <div class="control-group">
        <CascadeSelect
          :model-value="selection.chapter"
          :options="chapterOptions"
          :is-open="openSelectMenu === 'chapter'"
          placeholder="章節"
          :disabled="isChapterSelectDisabled"
          @update:model-value="selectChapter"
          @update:is-open="handleSelectMenuOpen('chapter')"
        />
      </div>

      <!-- 書籤按鈕 -->
      <button
        class="toolbar-button"
        :disabled="!isSelectionComplete && bookmarks.length === 0"
        @click="showBookmarkPanel = !showBookmarkPanel"
        title="添加書籤"
      >
        書籤
      </button>

      <!-- 筆記按鈕 -->
      <button
        class="toolbar-button"
        :disabled="!isSelectionComplete && notes.length === 0"
        @click="showNoteListPanel = !showNoteListPanel"
        title="筆記清單"
      >
        筆記
      </button>

      <!-- 強字號按鈕 -->
      <button
        class="toolbar-button"
        :class="{ 'active': strongsDict.isEnabled.value }"
        :disabled="geoHighlight.isEnabled.value"
        @click="strongsDict.toggleEnabled(); setIncludeStrong(strongsDict.isEnabled.value)"
        title="強字號字典"
      >
        強字號
      </button>

      <!-- 地名按鈕 -->
      <button
        class="toolbar-button"
        :class="{ 'active': geoHighlight.isEnabled.value }"
        :disabled="isGeoDisabled"
        @click="handleGeoToggle"
        title="地名反白"
      >
        地名
      </button>
    </div>

    <!-- 書籤面板 -->
    <div
      class="bookmark-panel"
      :class="{ 'is-open': showBookmarkPanel }"
      @click="showBookmarkPanel = false"
    >
      <div class="backdrop"></div>

      <div class="panel" @click.stop>
        <div class="panel-header">
          <span>書籤 ({{ bookmarks.length }})</span>
          <button class="close-btn" @click="showBookmarkPanel = false" title="關閉">
            ✕
          </button>
        </div>
        <BookmarkList
          :bookmarks="bookmarks"
          @delete="handleDeleteBookmark"
          @select="handleSelectBookmark"
        />
      </div>
    </div>

    <!-- 筆記清單側邊欄 -->
    <div
      class="note-list-backdrop"
      :class="{ 'is-open': showNoteListPanel }"
      @click="showNoteListPanel = false"
    >
      <div class="backdrop"></div>
      <div class="note-list-panel-wrapper" @click.stop>
        <div class="panel-header">
          <span>筆記列表 ({{ notes.length }})</span>
          <button class="close-btn" @click="showNoteListPanel = false" title="關閉">
            ✕
          </button>
        </div>
        <NoteList
          :notes="notes"
          :versions="versions"
          :books="books"
          :loading="false"
          @select="handleSelectNoteFromList"
          @delete="deleteNote"
        />
      </div>
    </div>

    <!-- 強字號字典側邊欄 -->
    <div
      class="strongs-panel"
      :class="{ 'is-open': strongsDict.showPanel.value }"
      @click="strongsDict.closePanel"
    >
      <div class="backdrop"></div>
      <div class="panel" @click.stop>
        <div class="panel-header">
          <span v-if="strongsDict.currentNumber.value">
            H{{ strongsDict.currentNumber.value }}
          </span>
          <span v-else>強字號字典</span>
          <button class="close-btn" @click="strongsDict.closePanel" title="關閉">
            ✕
          </button>
        </div>
        <StrongsPanel
          :entry="strongsDict.currentEntry.value"
          :loading="strongsDict.loading.value"
          :error="strongsDict.error.value"
          @select-related="strongsDict.lookupWord"
        />
      </div>
    </div>

    <!-- 經文閱讀框 -->
    <BibleReadingFrame
      :verses="verses"
      :loading="loading"
      :error="error"
      :selection="selection"
      :strongs-enabled="strongsDict.isEnabled.value"
      :geo-enabled="geoHighlight.isEnabled.value"
      :geo-places="geoHighlight.places.value"
      @submit="handleVerseSubmit"
      @strongs-click="strongsDict.lookupWord"
      @morph-click="handleMorphClick"
      @geo-click="handleGeoClick"
    />
  </div>
</template>

<style scoped>
.bible-reading-toolbar {
  display: flex;
  flex-direction: column;
  background-color: #fafafa;
  user-select: none;
  position: relative;
  height: 100%;
}

.toolbar-controls {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  align-content: flex-start;
  min-height: var(--ctrl-bar-height);
  border-bottom: 1px solid #e0e0e0;
  padding: 4px 12px;
  flex-shrink: 0;
}

.control-group {
  width: 120px;
  min-width: 100px;
  flex-shrink: 0;
}

.toolbar-button {
  padding: 4px 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background-color: white;
  color: #1f2937;
  font-size: var(--font-size-base);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  height: var(--btn-height);
  display: flex;
  align-items: center;
}

.toolbar-button:hover:not(:disabled) {
  background-color: #f3f4f6;
  border-color: #9ca3af;
}

.toolbar-button:disabled {
  background-color: #f9fafb;
  color: #9ca3af;
  cursor: not-allowed;
  border-color: #e5e7eb;
}

.bookmark-panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 2000;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s, visibility 0.3s;
  pointer-events: none;
}

.bookmark-panel.is-open {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
}

.bookmark-panel .backdrop {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.3);
}

.bookmark-panel .panel {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 320px;
  max-width: 100%;
  background-color: white;
  box-shadow: -2px 0 12px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  animation: slideIn 0.3s ease-out;
}

.bookmark-panel .panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #e0e0e0;
  background-color: #f5f5f5;
  font-weight: 600;
  color: #1f2937;
  font-size: calc(var(--font-size-base) * 0.85);
}

.bookmark-panel .close-btn {
  background: none;
  border: none;
  cursor: pointer;
  font-size: calc(var(--font-size-base) * 1.1);
  color: #666;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.bookmark-panel .close-btn:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  border-bottom: 1px solid #e5e7eb;
  font-weight: 600;
  color: #1f2937;
  background-color: #f9fafb;
}

.close-btn {
  background: none;
  border: none;
  color: #6b7280;
  font-size: calc(var(--font-size-base) * 1.25);
  cursor: pointer;
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
}

.close-btn:hover {
  color: #1f2937;
}

.panel-content {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.panel-item {
  padding: 8px;
  color: #6b7280;
  font-size: 13px;
  text-align: center;
}

.note-list-backdrop {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 2000;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s, visibility 0.3s;
  pointer-events: none;
}

.note-list-backdrop.is-open {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
}

.note-list-backdrop .backdrop {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.3);
}

.note-list-panel-wrapper {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 320px;
  max-width: 100%;
  background-color: white;
  box-shadow: -2px 0 12px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  animation: slideIn 0.3s ease-out;
}

/* 強字號面板樣式 */
.strongs-panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 2000;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s, visibility 0.3s;
  pointer-events: none;
}

.strongs-panel.is-open {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
}

.strongs-panel .backdrop {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.3);
}

.strongs-panel .panel {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 320px;
  max-width: 100%;
  background-color: white;
  box-shadow: -2px 0 12px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  animation: slideIn 0.3s ease-out;
}

.strongs-panel .panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
  background-color: #f5f5f5;
  font-weight: 600;
  color: #1f2937;
  font-size: var(--font-size-base);
  gap: 8px;
  flex-wrap: wrap;
}

.strongs-panel .header-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.morphology-toggle {
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: 400;
  cursor: pointer;
  font-size: 11px;
}

.morphology-toggle input[type="checkbox"] {
  width: 14px;
  height: 14px;
  cursor: pointer;
}

.strongs-panel .close-btn {
  background: none;
  border: none;
  cursor: pointer;
  font-size: calc(var(--font-size-base) * 1.1);
  color: #666;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.strongs-panel .close-btn:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.toolbar-button.active {
  background-color: #e0e7ff;
  border-color: #3b82f6;
  color: #3b82f6;
}

</style>
